import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import MapContainer from './components/Map/MapContainer';
import StationMarker from './components/Map/StationMarker';
import VisualClusterMarker from './components/Map/VisualClusterMarker';
import LocationMarker from './components/Map/LocationMarker';
import MapNavigation from './components/Map/MapNavigation';
import VehicleConfigModal from './components/VehicleConfig/VehicleConfigModal';
import StationListPanel from './components/StationListPanel/StationListPanel';
import AboutModal from './components/AboutModal/AboutModal';
import AppHeader from './components/AppHeader/AppHeader';
import SearchParameters from './components/SearchParameters/SearchParameters';
import MobileResultsView from './components/MobileResults/MobileResultsView';
import LandingPage from './components/LandingPage/LandingPage';
import { useLocationSelection } from './hooks/useGeolocation';
import { useStationRecommendations, useViewportStationsWithPrices, useVisualClustering } from './hooks/useStations';
import { useHealthCheck } from './hooks/useHealthCheck';
import { useVehicleStore } from './stores/vehicleStore';
import { GeolocationService } from './services/geolocation';
import { clusteringConfigService } from './services/clusteringConfig';
import { trackLocationSearch, trackCurrentLocationUsed } from './services/analytics';
import { calculateDistance, formatPrice } from './utils/formatters';
import type { SearchFilters, StationRecommendation, Location, ClusterData } from './types';
import type { VisualClusterData } from './utils/visualClustering';

const DEFAULT_FILTERS: SearchFilters = {
  fuelTypes: ['B7'],
  brands: [],
  radius: 10,
  sortBy: 'total_cost',
  tripType: 'single',
};

const DARK_TONES: Record<string, { c0: string; c1: string; line: string; lineStrong: string; fg: string; mute: string; dim: string }> = {
  ink:      { c0: '#07090f', c1: '#0b0f1a', line: '#1a2335', lineStrong: '#2e3a55', fg: '#f4f6fb', mute: '#aab3c5', dim: '#6b7589' },
  charcoal: { c0: '#0d0d10', c1: '#141417', line: '#222227', lineStrong: '#2c2c33', fg: '#f2f2f4', mute: '#9a9aa8', dim: '#606068' },
  forest:   { c0: '#06100c', c1: '#0a1813', line: '#16302a', lineStrong: '#22433a', fg: '#e8f4ef', mute: '#8ab3a0', dim: '#527d6a' },
  plum:     { c0: '#0c0710', c1: '#140e1c', line: '#241a36', lineStrong: '#3a2c50', fg: '#f0eaf8', mute: '#a898c0', dim: '#6d5d8a' },
  espresso: { c0: '#0e0a07', c1: '#181210', line: '#2a201a', lineStrong: '#3a2c24', fg: '#f5f0eb', mute: '#b0a090', dim: '#7a6a5a' },
};

// ── Stat chip for the stats strip ─────────────────────────────────────────────

function StatChip({ label, value, color, trend, onClick }: {
  label: string;
  value: string;
  color?: string;
  trend?: '↓' | '↑';
  onClick?: () => void;
}) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--paper-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
      <div
        className="mono"
        onClick={onClick}
        style={{
          fontSize: 13.5, fontWeight: 600,
          color: color || 'var(--paper-fg)', marginTop: 2,
          display: 'inline-flex', alignItems: 'center', gap: 4,
          cursor: onClick ? 'pointer' : 'default',
          textDecoration: onClick ? 'underline' : 'none',
          textDecorationColor: 'transparent',
        }}
        onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.textDecorationColor = color || 'var(--paper-fg)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecorationColor = 'transparent'; }}
      >
        {trend && <span style={{ fontSize: 11 }}>{trend}</span>}
        {value}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

function App() {
  const [isVehicleConfigOpen, setIsVehicleConfigOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
  const [selectedStation, setSelectedStation] = useState<StationRecommendation | null>(null);
  const [fuelAmount, setFuelAmount] = useState<number>(50);
  const [mapZoom, setMapZoom] = useState<number>(6);
  const [targetZoom, setTargetZoom] = useState<number | null>(null);
  const [mapBounds, setMapBounds] = useState<{ south: number; north: number; west: number; east: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<Location | null>(null);
  const [shouldUpdateMapView, setShouldUpdateMapView] = useState<boolean>(false);

  const explorationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [stableExplorationState, setStableExplorationState] = useState<boolean>(false);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [realMapSize, setRealMapSize] = useState<{ width: number; height: number }>({ width: 800, height: 600 });

  const [clusteringThreshold, setClusteringThreshold] = useState<number>(11);
  const [personalisationThreshold, setPersonalisationThreshold] = useState<number>(2);
  const [isStationNavigation, setIsStationNavigation] = useState(false);

  const { vehicleConfig, setCostPerMile } = useVehicleStore();
  const { lastSync, healthData } = useHealthCheck();
  const {
    selectedLocation,
    isLoading: locationLoading,
    hasLocation,
    locationDisplayName,
    setSelectedLocation,
    useUserLocation,
    clearError,
    reset,
  } = useLocationSelection();

  const {
    data: recommendations = [],
    averageFuelPrice = 0,
    minFuelPrice = 0,
    maxFuelPrice = 0,
    priceGap = 0,
    minPriceStation = null,
    maxPriceStation = null,
    isLoading: recommendationsLoading,
  } = useStationRecommendations(selectedLocation, vehicleConfig, filters, fuelAmount);

  const { p25FuelPrice, p75FuelPrice } = useMemo(() => {
    const prices = recommendations
      .map(r => r.bestPrice?.price_pence)
      .filter((p): p is number => p !== undefined)
      .sort((a, b) => a - b);
    if (prices.length === 0) return { p25FuelPrice: 0, p75FuelPrice: 0 };
    return {
      p25FuelPrice: prices[Math.floor(prices.length * 0.25)],
      p75FuelPrice: prices[Math.floor(prices.length * 0.75)],
    };
  }, [recommendations]);

  const shouldFetchViewportStations = true;

  const rawExplorationState = useMemo(() => {
    if (!hasLocation || !selectedLocation) return false;
    if (!mapCenter) return false;
    if (isStationNavigation) return false;
    const distanceFromSelected = calculateDistance(
      selectedLocation.latitude, selectedLocation.longitude,
      mapCenter.latitude, mapCenter.longitude
    );
    return distanceFromSelected > personalisationThreshold;
  }, [hasLocation, selectedLocation, mapCenter, personalisationThreshold, isStationNavigation]);

  useEffect(() => {
    if (explorationTimeoutRef.current) clearTimeout(explorationTimeoutRef.current);
    explorationTimeoutRef.current = setTimeout(() => setStableExplorationState(rawExplorationState), 300);
    return () => { if (explorationTimeoutRef.current) clearTimeout(explorationTimeoutRef.current); };
  }, [rawExplorationState]);

  const isExploringAwayFromLocation = stableExplorationState;

  useEffect(() => {
    const loadConfig = async () => {
      try {
        await clusteringConfigService.loadConfig();
        setClusteringThreshold(await clusteringConfigService.getClusteringThreshold());
        setPersonalisationThreshold(await clusteringConfigService.getPersonalisationThreshold());
      } catch { /* use defaults */ }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const updateMapSize = () => {
      if (mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        setRealMapSize(prev => {
          const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
          return prev.width !== next.width || prev.height !== next.height ? next : prev;
        });
      }
    };
    updateMapSize();
    const ro = new ResizeObserver(updateMapSize);
    ro.observe(mapContainerRef.current);
    return () => ro.disconnect();
  }, []);

  const shouldShowViewportStations = mapZoom > clusteringThreshold;
  const shouldShowPersonalisedStations = hasLocation && !isExploringAwayFromLocation;
  const shouldShowVisualClustering = !shouldShowPersonalisedStations;

  const { data: viewportStationsWithPrices = [] } = useViewportStationsWithPrices(
    mapBounds || undefined, filters.fuelTypes[0] || 'B7', shouldFetchViewportStations, selectedLocation
  );

  const { clusters: visualClusters, individualStations, priceRange } = useVisualClustering(
    viewportStationsWithPrices, mapZoom, mapCenter, realMapSize,
    {
      minClusterSize: 2, padding: 8, maxStations: 1000,
      enabled: mapBounds !== null && mapCenter !== null,
      fuelCode: filters.fuelTypes[0] || 'B7',
      mapBounds: mapBounds || undefined,
      clusteringThreshold,
    }
  );

  const { data: viewportRecommendations = [] } = useViewportStationsWithPrices(
    mapBounds || undefined, filters.fuelTypes[0] || 'B7', shouldShowViewportStations
  );

  const handleLocationSelect = useCallback((location: Location, displayName: string) => {
    setSelectedLocation(location, displayName);
    setTargetZoom(null);
    setShouldUpdateMapView(true);
    setMapCenter(location);
    trackLocationSearch(displayName);
    clearError();
  }, [setSelectedLocation, clearError]);

  const handleUseCurrentLocation = useCallback(() => {
    clearError();
    useUserLocation();
    trackCurrentLocationUsed();
  }, [clearError, useUserLocation]);

  const handleStationSelect = useCallback((recommendation: StationRecommendation) => {
    setIsStationNavigation(true);
    setSelectedStation(recommendation);
    setMapCenter({ latitude: recommendation.station.location.latitude, longitude: recommendation.station.location.longitude });
    setTargetZoom(12);
    setShouldUpdateMapView(true);
  }, []);

  const handleUserDrag = useCallback(() => setIsStationNavigation(false), []);

  const handleStationSelectFromMap = useCallback((recommendation: StationRecommendation) => {
    setSelectedStation(recommendation);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<SearchFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleFuelAmountChange = useCallback((amount: number) => setFuelAmount(amount), []);

  const handleMinPriceClick = useCallback((station: StationRecommendation) => handleStationSelect(station), [handleStationSelect]);
  const handleMaxPriceClick = useCallback((station: StationRecommendation) => handleStationSelect(station), [handleStationSelect]);

  const handleMapZoomChange = useCallback((zoomLevel: number) => setMapZoom(zoomLevel), []);

  const handleMapBoundsChange = useCallback((bounds: { south: number; north: number; west: number; east: number }) => {
    setMapBounds(bounds);
    setMapCenter({
      latitude: (bounds.south + bounds.north) / 2,
      longitude: (bounds.west + bounds.east) / 2,
    });
  }, []);

  const handleClusterClick = useCallback((cluster: ClusterData | VisualClusterData) => {
    const center = 'center' in cluster ? cluster.center : null;
    if (center) setMapCenter(center);
    setTargetZoom(Math.min(mapZoom + 3, 18));
    setShouldUpdateMapView(true);
  }, [mapZoom]);

  const handleReturnToLocation = useCallback(() => {
    if (selectedLocation) {
      setMapCenter(selectedLocation);
      setTargetZoom(12);
      setShouldUpdateMapView(true);
    }
  }, [selectedLocation]);

  const handleReset = useCallback(() => {
    reset();
    setFilters(DEFAULT_FILTERS);
    setSelectedStation(null);
    setFuelAmount(50);
    setMapZoom(6);
    setTargetZoom(null);
    setMapCenter(null);
    setMapBounds(null);
    setShouldUpdateMapView(true);
    setStableExplorationState(false);
    if (explorationTimeoutRef.current) clearTimeout(explorationTimeoutRef.current);
  }, [reset]);

  const initialMapCenter = useMemo(
    () => selectedLocation || GeolocationService.getUKCenter(),
    [selectedLocation]
  );

  useEffect(() => {
    if (shouldUpdateMapView) {
      const timer = setTimeout(() => setShouldUpdateMapView(false), 100);
      return () => clearTimeout(timer);
    }
  }, [shouldUpdateMapView]);

  useEffect(() => {
    if (selectedLocation) {
      setMapCenter(selectedLocation);
      setTargetZoom(null);
      setShouldUpdateMapView(true);
    }
  }, [selectedLocation]);

  // ── Dark theme sync (matches landing page tweaks) ─────────────────────────────
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('fh_tweaks') || '{}');
      const t = DARK_TONES[stored.bgTone as string] || DARK_TONES.ink;
      const root = document.documentElement;
      root.style.setProperty('--paper', t.c0);
      root.style.setProperty('--paper-2', t.c1);
      root.style.setProperty('--paper-line', t.line);
      root.style.setProperty('--paper-line-strong', t.lineStrong);
      root.style.setProperty('--paper-fg', t.fg);
      root.style.setProperty('--paper-mute', t.mute);
      root.style.setProperty('--paper-dim', t.dim);
      if (stored.accent) root.style.setProperty('--amber', stored.accent);
      if (stored.savingsAccent) root.style.setProperty('--green', stored.savingsAccent);
    } catch { /* ignore malformed tweaks */ }
  }, []);

  // ── Render: Landing page when no location selected ──────────────────────────

  if (!hasLocation) {
    return (
      <>
        <LandingPage
          onLocationSelect={handleLocationSelect}
          onUseCurrentLocation={handleUseCurrentLocation}
          isLoadingCurrentLocation={locationLoading}
          onAboutModalOpen={() => setIsAboutModalOpen(true)}
          totalStations={healthData?.total_stations}
        />
        <VehicleConfigModal isOpen={isVehicleConfigOpen} onClose={() => setIsVehicleConfigOpen(false)} />
        <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
      </>
    );
  }

  // ── Render: Results page ────────────────────────────────────────────────────

  // Stats for the strip
  const spread = maxFuelPrice > minFuelPrice ? (maxFuelPrice - minFuelPrice).toFixed(1) : null;
  const stationCount = shouldShowPersonalisedStations ? recommendations.length : viewportRecommendations.length;

  return (
    <>
      {/* ── Mobile results view (< lg) ── */}
      <MobileResultsView
        recommendations={recommendations}
        selectedStation={selectedStation}
        filters={filters}
        fuelAmount={fuelAmount}
        averageFuelPrice={averageFuelPrice}
        costPerMile={vehicleConfig.costPerMile}
        locationDisplayName={locationDisplayName}
        selectedLocation={selectedLocation}
        minFuelPrice={minFuelPrice}
        maxFuelPrice={maxFuelPrice}
        onStationSelect={handleStationSelect}
        onFilterChange={handleFilterChange}
        onFuelAmountChange={handleFuelAmountChange}
        onCostPerMileChange={setCostPerMile}
        onReset={handleReset}
        isLoading={recommendationsLoading}
        mapCenter={mapCenter || initialMapCenter}
        targetZoom={targetZoom ?? (hasLocation ? 12 : 6)}
        onMapZoomChange={handleMapZoomChange}
        onMapBoundsChange={handleMapBoundsChange}
        shouldUpdateMapView={shouldUpdateMapView}
        p25FuelPrice={p25FuelPrice}
        p75FuelPrice={p75FuelPrice}
        priceRange={priceRange}
        visualClusters={visualClusters}
        individualStations={individualStations}
        shouldShowVisualClustering={shouldShowVisualClustering}
        shouldShowPersonalisedStations={shouldShowPersonalisedStations}
        onClusterClick={handleClusterClick}
        onUserDrag={handleUserDrag}
      />

      {/* ── Desktop layout (lg+) ── */}
      <div className="hidden lg:flex lg:flex-col" style={{ background: 'var(--paper)', minHeight: '100vh', color: 'var(--paper-fg)' }}>

        {/* TopBar */}
        <AppHeader
          onLocationSelect={handleLocationSelect}
          onUseCurrentLocation={handleUseCurrentLocation}
          isLoadingCurrentLocation={locationLoading}
          onReset={handleReset}
          onAboutModalOpen={() => setIsAboutModalOpen(true)}
        />

        {/* Filter rail */}
        <div style={{
          background: 'var(--paper-2)',
          borderBottom: '1px solid var(--paper-line)',
          position: 'sticky', top: 60, zIndex: 40,
        }}>
          <SearchParameters
            filters={filters}
            fuelAmount={fuelAmount}
            vehicleConfig={vehicleConfig}
            recommendations={recommendations}
            averageFuelPrice={averageFuelPrice}
            minFuelPrice={minFuelPrice}
            maxFuelPrice={maxFuelPrice}
            priceGap={priceGap}
            minPriceStation={minPriceStation}
            maxPriceStation={maxPriceStation}
            recommendationsLoading={recommendationsLoading}
            onFilterChange={handleFilterChange}
            onFuelAmountChange={handleFuelAmountChange}
            onCostPerMileChange={setCostPerMile}
            onMinPriceClick={handleMinPriceClick}
            onMaxPriceClick={handleMaxPriceClick}
          />
        </div>

        {/* Stats strip */}
        <div style={{
          background: 'var(--paper-2)',
          borderBottom: '1px solid var(--paper-line)',
          padding: '10px 28px',
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--green-soft)', color: 'var(--green)',
            }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--paper-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Location</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginTop: 1 }}>
                {isExploringAwayFromLocation ? 'Exploring map' : (locationDisplayName ?? 'Your location')}
              </div>
            </div>
          </div>

          <div style={{ width: 1, height: 28, background: 'var(--paper-line)', flexShrink: 0 }} />

          <StatChip label="Stations found" value={String(stationCount)} />
          {minFuelPrice > 0 && (
            <>
              <StatChip
                label="Cheapest"
                value={`${formatPrice(minFuelPrice)}/L`}
                color="var(--green)"
                trend="↓"
                onClick={minPriceStation ? () => handleMinPriceClick(minPriceStation) : undefined}
              />
              <StatChip label="Area average" value={`${formatPrice(averageFuelPrice)}/L`} />
              <StatChip
                label="Most expensive"
                value={`${formatPrice(maxFuelPrice)}/L`}
                color="var(--red)"
                trend="↑"
                onClick={maxPriceStation ? () => handleMaxPriceClick(maxPriceStation) : undefined}
              />
              {spread && <StatChip label="Spread" value={`${spread}p/L`} />}
            </>
          )}

          <div style={{ flex: 1 }} />

          {isExploringAwayFromLocation && locationDisplayName && (
            <button
              onClick={handleReturnToLocation}
              style={{
                fontSize: 12.5, color: 'var(--amber)', background: 'none', border: 'none',
                cursor: 'pointer', fontWeight: 600, textDecoration: 'underline',
              }}
            >
              Return to {locationDisplayName}
            </button>
          )}
        </div>

        {/* Map + List split */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr',
          height: 'calc(100vh - 60px - 120px - 50px)',
          minHeight: 600,
        }}>
          <div ref={mapContainerRef} style={{ position: 'relative', overflow: 'hidden', borderRight: '1px solid var(--paper-line)' }}>
            <MapContainer
              center={mapCenter || initialMapCenter}
              zoom={targetZoom ?? (hasLocation ? 12 : 6)}
              height="100%"
              className="w-full h-full"
              onZoomChange={handleMapZoomChange}
              onBoundsChange={handleMapBoundsChange}
              onUserDrag={handleUserDrag}
              shouldUpdateView={shouldUpdateMapView}
            >
              {selectedStation && (
                <StationMarker
                  key={`station-${selectedStation.station.id}`}
                  recommendation={selectedStation}
                  isSelected={true}
                  onClick={handleStationSelectFromMap}
                  tripType={filters.tripType}
                  selectedLocation={selectedLocation}
                  p25FuelPrice={p25FuelPrice}
                  p75FuelPrice={p75FuelPrice}
                />
              )}
              {shouldShowVisualClustering && visualClusters.map((cluster) => (
                <VisualClusterMarker
                  key={`visual-cluster-${cluster.id}`}
                  cluster={cluster}
                  onClick={handleClusterClick}
                  priceRange={priceRange}
                />
              ))}
              {shouldShowVisualClustering && individualStations
                .filter(r => r.station.id !== selectedStation?.station.id)
                .map((recommendation) => (
                  <StationMarker
                    key={`station-${recommendation.station.id}`}
                    recommendation={recommendation}
                    isSelected={false}
                    onClick={handleStationSelectFromMap}
                    tripType={filters.tripType}
                    selectedLocation={selectedLocation}
                    p25FuelPrice={p25FuelPrice}
                    p75FuelPrice={p75FuelPrice}
                  />
                ))}
              {shouldShowPersonalisedStations && recommendations
                .filter(r => r.station.id !== selectedStation?.station.id)
                .map((recommendation) => (
                  <StationMarker
                    key={`station-${recommendation.station.id}`}
                    recommendation={recommendation}
                    isSelected={false}
                    onClick={handleStationSelectFromMap}
                    tripType={filters.tripType}
                    selectedLocation={selectedLocation}
                    p25FuelPrice={p25FuelPrice}
                    p75FuelPrice={p75FuelPrice}
                  />
                ))}
              {hasLocation && selectedLocation && (
                <LocationMarker location={selectedLocation} displayName={locationDisplayName} />
              )}
            </MapContainer>
            <MapNavigation
              isExploring={isExploringAwayFromLocation}
              hasLocation={hasLocation}
              onReturnToLocation={handleReturnToLocation}
              onReset={handleReset}
            />
          </div>

          <StationListPanel
            recommendations={recommendations}
            selectedStation={selectedStation}
            onStationSelect={handleStationSelect}
            isLoading={recommendationsLoading}
            className="h-full rounded-none border-0 shadow-none"
            tripType={filters.tripType}
            sortBy={filters.sortBy}
            p25FuelPrice={p25FuelPrice}
            p75FuelPrice={p75FuelPrice}
          />
        </div>

        {/* Footer */}
        <footer style={{
          background: 'var(--paper-2)', borderTop: '1px solid var(--paper-line)',
          padding: '14px 28px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 12.5, color: 'var(--paper-mute)',
          flexWrap: 'wrap', gap: 8,
        }}>
          <span>© 2026 FuelHunt · UK fuel price tracker</span>
          <span className="mono">
            {lastSync ? `Data updated ${new Date(lastSync).toLocaleDateString('en-GB')}` : 'Data updated regularly'} · 14 retailers · No registration required
          </span>
        </footer>
      </div>

      <VehicleConfigModal isOpen={isVehicleConfigOpen} onClose={() => setIsVehicleConfigOpen(false)} />
      <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
    </>
  );
}

export default App;
